import { useEffect } from 'preact/hooks'
import { X, Mail, ArrowRight } from 'lucide-preact'
import { PersonAvatar } from '@/components/common/Avatar'
import { CopyButton } from '@/components/common/CopyButton'
import { formatDateTime } from '@/components/webs/email-blasts/engagementFormat'
import { InviteAccessBadge, InviteDeliveryBadge } from './InviteStatusBadge'
import { canReInvite } from './inviteStatus'
import { RoleBadge } from './RoleBadge'
import type { ClientCompanyViewDto } from '@/types/company'
import type { RoleType, UserInviteStatusDto, UserProfileWithRolesDto } from '@/types/userRoles'

interface Props {
  userProfile: UserProfileWithRolesDto
  status?: UserInviteStatusDto
  workspaces: ClientCompanyViewDto['Workspaces']
  reInviting: boolean
  onReInvite: (userProfile: UserProfileWithRolesDto) => void
  onClose: () => void
}

/**
 * Read-only detail for one person: whether they can get in, what became of their invitation email, and the
 * roles they hold. Chrome mirrors the email activity flyout, which answers the same shape of question.
 *
 * Read-only on purpose — editing roles stays in its own surface. Status belongs where it can be looked at,
 * not inside an editor that cannot change it.
 */
export function UserDetailFlyout({ userProfile, status, workspaces, reInviting, onReInvite, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const displayName = `${userProfile.FirstName} ${userProfile.LastName}`.trim() || userProfile.Email
  const roleSet = userProfile.UserCompanyRoleSet
  const delivery = status?.Delivery ?? undefined
  const showReInvite = status ? canReInvite(status.AccessState, delivery?.State) : false

  return (
    <div className="fixed inset-0 z-50 flex" aria-modal="true" role="dialog" aria-label={`Details for ${displayName}`}>
      <div className="flex-1 bg-slate-900/40" onClick={onClose} />
      <aside className="ml-auto flex h-full w-full flex-col border-l border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-xl sm:w-[440px]">
        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-5 py-3">
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">User detail</span>
          <button
            type="button"
            onClick={onClose}
            className="grid h-7 w-7 place-items-center rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto p-5">
          <div className="flex items-start gap-3">
            <PersonAvatar firstName={userProfile.FirstName} lastName={userProfile.LastName} profilePicUrl={userProfile.AvatarUrl} size="lg" />
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-base font-semibold text-slate-900 dark:text-white">{displayName}</h2>
              <p className="truncate text-sm text-slate-500 dark:text-slate-400">{userProfile.Email}</p>
              {status && <div className="mt-1.5"><InviteAccessBadge state={status.AccessState} /></div>}
            </div>
          </div>

          {status?.HomeCompanyName && (
            <p className="rounded-lg bg-slate-50 dark:bg-slate-800/60 px-3 py-2.5 text-sm text-slate-600 dark:text-slate-300">
              Belongs to <span className="font-medium text-slate-800 dark:text-slate-100">{status.HomeCompanyName}</span> and reaches
              this company through their roles, so there is no invitation to send here.
            </p>
          )}

          {status && !status.HomeCompanyName && (
            <dl className="divide-y divide-slate-100 rounded-lg border border-slate-200 text-sm dark:divide-slate-800/70 dark:border-slate-800">
              <Row label="Invited">{status.InvitedAtUtc ? formatDateTime(status.InvitedAtUtc) : '—'}</Row>
              <Row label="Link expires">{status.ExpiresAtUtc ? formatDateTime(status.ExpiresAtUtc) : '—'}</Row>
              <Row label="Email">{delivery ? <InviteDeliveryBadge state={delivery.State} /> : '—'}</Row>
              {delivery?.FailureReason && <Row label="Reason">{delivery.FailureReason}</Row>}
              {delivery?.DeliveredAtUtc && <Row label="Delivered">{formatDateTime(delivery.DeliveredAtUtc)}</Row>}
              {delivery?.BouncedAtUtc && <Row label="Bounced">{formatDateTime(delivery.BouncedAtUtc)}</Row>}
            </dl>
          )}

          {delivery?.RfcMessageId && (
            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Message ID</h3>
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2">
                <code className="min-w-0 flex-1 truncate text-xs text-slate-600 dark:text-slate-300">{delivery.RfcMessageId}</code>
                <CopyButton text={delivery.RfcMessageId} label="Message ID" />
              </div>
              <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                Their own mail administrator can search for this to find the exact message.
              </p>
            </div>
          )}

          {delivery && !delivery.RfcMessageId && delivery.State !== 'Delivered' && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              No message ID — nothing was handed to the mail provider, so there is no message for their
              administrator to trace.
            </p>
          )}

          <div>
            <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 dark:text-slate-500">Roles</h3>
            {roleSet && roleSet.CompanyRoles.length > 0 && (
              <div className="mb-3">
                <div className="mb-1.5 text-sm text-slate-500 dark:text-slate-400">Company <span className="text-xs">(overrides workspace roles)</span></div>
                <div className="flex flex-wrap gap-1">
                  {roleSet.CompanyRoles.map((role: RoleType) => <RoleBadge key={role} role={role} />)}
                </div>
              </div>
            )}
            {roleSet && roleSet.WorkspaceRoles.length > 0 ? (
              <div className="space-y-2">
                {roleSet.WorkspaceRoles.map(wsRoles => {
                  const workspace = workspaces?.find(w => w.Id === wsRoles.WorkspaceId)
                  return (
                    <div key={wsRoles.WorkspaceId} className="text-sm">
                      <div className="font-medium text-slate-800 dark:text-slate-200">{workspace?.Name || 'Unknown workspace'}</div>
                      <div className="mt-1 flex flex-wrap gap-1">
                        {wsRoles.Roles.map((role: RoleType) => (
                          <RoleBadge key={role} role={role} isOverridden={roleSet.CompanyRoles.length > 0} />
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            ) : (
              (!roleSet || roleSet.CompanyRoles.length === 0) && (
                <span className="text-sm text-slate-500 dark:text-slate-400">No roles assigned</span>
              )
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-slate-200 px-5 py-3 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Close
          </button>
          {showReInvite && (
            <button
              type="button"
              onClick={() => onReInvite(userProfile)}
              disabled={reInviting}
              className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Mail size={14} />
              {reInviting ? 'Sending...' : 'Re-invite'}
              {!reInviting && <ArrowRight size={14} />}
            </button>
          )}
        </div>
      </aside>
    </div>
  )
}

function Row({ label, children }: { label: string; children: preact.ComponentChildren }) {
  return (
    <div className="flex items-center justify-between gap-3 px-3 py-2">
      <dt className="text-slate-500 dark:text-slate-400 flex-shrink-0">{label}</dt>
      <dd className="min-w-0 truncate text-right font-medium text-slate-800 dark:text-slate-200">{children}</dd>
    </div>
  )
}
